import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import type { StackProps } from "@mui/material/Stack";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Pagination from "@mui/material/Pagination";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import PrintIcon from "@mui/icons-material/Print";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import MoveToInboxIcon from "@mui/icons-material/MoveToInbox";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import AssignmentReturnedIcon from "@mui/icons-material/AssignmentReturned";
import useUkm from "../../services/queries/useUkm";
import useItem from "../../services/queries/useItem";
import useUser from "../../services/queries/useUser";
import useKategori from "../../services/queries/useKategori";
import ModalStok from "../../components/ModalStok/ModalStok";
import ModalDetailBarang from "../../components/ModalDetailBarang/ModalDetailBarang";
import toRupiah from "../../utils/toRupiah";
import avatarAlt from "../../utils/avatarAlt";
import { getLocalUkmId } from "../../utils/handleChangeUkmId";
import { useAuthStore } from "../../stores/authStore";
import { useTransaksiStore } from "../../stores/transaksiStore";
import {
    ContentGetItemResponse,
    IOptions,
    ModalStokField,
    ParamsGetAssetRevenue,
    ParamsGetItem,
    ParamsGetKategori,
    SelectedProductBundling,
} from "../../constants/types";
import { ReactComponent as BarangTidakDitemukan } from "../../assets/images/BarangTidakDitemukan.svg";
import Spinner from "../Spinner/Spinner";
import NoRowsImage from "../NoRowsImage/NoRowsImage";
import LoadingTabel from "../LoadingTabel/LoadingTabel";
import CardStokBarang from "../CardStokBarang/CardStokBarang";
import ModalDeleteStok from "../ModalDeleteStok/ModalDeleteStok";
import ModalDeleteMultipleStok from "../ModalDeleteMultipleStok/ModalDeleteMultipleStok";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { Search } from "@mui/icons-material";
import truncate from "../../utils/truncate";
import toRibuan from "../../utils/toRibuan";
import moment from "moment";
import "moment/locale/id";
import FormControl from "@mui/material/FormControl";
import { Tooltip } from "@mui/material";
import { mapStockFieldSorting } from "../../utils/mapStockFieldSorting";
import Autocomplete from "@mui/material/Autocomplete";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CloseIcon from "@mui/icons-material/Close";

import Swal from "sweetalert2";
import Collapse from "@mui/material/Collapse";
import Skeleton from "@mui/material/Skeleton";
import useAssetRevenue from "../../services/queries/useAssetRevenue";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import useUrlState from "../../hooks/useUrlState";

moment.locales();
moment.locale("id");

const tabelBarangSchema = {
    search: {
        defaultValue: "",
        parse: (raw: string) => raw,
        serialize: (v: string) => v,
    },
    kategori: {
        defaultValue: "",
        parse: (raw: string) => raw,
        serialize: (v: string) => v,
    },
    page: {
        defaultValue: 1,
        parse: (raw: string) => {
            const n = parseInt(raw, 10);
            return isNaN(n) || n < 1 ? null : n;
        },
        serialize: (v: number) => String(v),
    },
    pageSize: {
        defaultValue: 10,
        parse: (raw: string) => {
            const n = parseInt(raw, 10);
            return isNaN(n) || n < 1 ? null : n;
        },
        serialize: (v: number) => String(v),
    },
    sortColumn: {
        defaultValue: "",
        parse: (raw: string) => raw,
        serialize: (v: string) => v,
    },
    sortDir: {
        defaultValue: "",
        parse: (raw: string) => raw,
        serialize: (v: string) => v,
    },
};

interface ITabelBarangProps {
    onOpenKategori: () => void;
    onOpenKatalog: () => void;
    onOpenImport: () => void;
    onOpenMutasi: () => void;
    onOpenPrintBarcode: (data: {
        sku?: string | null;
        namaBarang: string;
        hargaBarang: string;
    }) => void;
}

type ActionVariant = "phone" | "tablet" | "desktop";

interface ActionButtonConfig {
    key: string;
    label: string;
    icon: React.ElementType;
    onClick: () => void;
    permissionId?: number;
}

interface StockLimitInfo {
    label: string;
    color: string;
    fontWeight?: string | number;
}

const TabelBarang = ({
    onOpenKategori,
    onOpenKatalog,
    onOpenImport,
    onOpenMutasi,
    onOpenPrintBarcode,
}: ITabelBarangProps) => {
    const theme = useTheme();
    const ukmIdUser = useAuthStore((s) => s.ukmIdUser);
    const isPhoneScreen = useMediaQuery(theme.breakpoints.between("xs", "sm"));
    const isTabletScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
    const isLaptopScreen = useMediaQuery(theme.breakpoints.up("md"));

    const ukmIdFromAuth = ukmIdUser && ukmIdUser[0];
    const [urlState, setUrlState] = useUrlState(tabelBarangSchema);
    const [selectedKategori, setSelectedKategori] = useState<IOptions | null>(null);
    const [searchInput, setSearchInput] = useState(() => urlState.search);
    const [sortModel, setSortModel] = useState<GridSortModel>([]);

    const debouncedSearchInput = useDebouncedValue(searchInput, 400);
    const isSearchMountRef = useRef(true);

    useEffect(() => {
        if (isSearchMountRef.current) {
            isSearchMountRef.current = false;
            return;
        }
        setUrlState({ search: debouncedSearchInput, page: 1 });
    }, [debouncedSearchInput, setUrlState]);

    const itemParams = useMemo<ParamsGetItem>(
        () => ({
            ukmId: ukmIdFromAuth,
            size: urlState.pageSize,
            page: urlState.page,
            isShow: 0,
            search: urlState.search,
            kategori: urlState.kategori,
            column: (urlState.sortColumn || undefined) as ParamsGetItem["column"],
            sortby: (urlState.sortDir || undefined) as ParamsGetItem["sortby"],
        }),
        [urlState, ukmIdFromAuth],
    );

    const {
        data: dataItem,
        refetch: refetchItem,
        isLoading: isLoadingItem,
        isError: isErrorItem,
    } = useItem(itemParams);

    const assetRevenueParams = useMemo<ParamsGetAssetRevenue>(
        () => ({
            ukmId: ukmIdFromAuth,
            kategori: urlState.kategori,
            subKategori: "",
        }),
        [urlState.kategori, ukmIdFromAuth],
    );

    const { data: asetRevenue, isLoading: isLoadingAsetRevenue } =
        useAssetRevenue(assetRevenueParams);

    const kategoriParams = useMemo<ParamsGetKategori>(
        () => ({
            ukmId: Number(ukmIdFromAuth),
            size: 500,
            page: 1,
            search: "",
        }),
        [ukmIdFromAuth],
    );
    const {
        status: statusKategori,
        data: dataKategori,
        refetch: refetchKategori,
        isError: isErrorKategori,
    } = useKategori(kategoriParams);

    useEffect(() => {
        if (!dataKategori) return;
        const match =
            dataKategori.content.find(
                (k) => String(k.id) === urlState.kategori,
            ) ?? null;
        setSelectedKategori(match);
    }, [urlState.kategori, dataKategori]);

    const initialParamsUkm = {
        size: 500,
        page: 1,
        search: "",
    };
    const { data: ukm } = useUkm(initialParamsUkm);
    const { data: user } = useUser();
    const ukmId = React.useMemo(() => {
        if (user?.userId) {
            return getLocalUkmId(user?.userId);
        }
        return null;
    }, [user?.userId]);

    const ukmData = React.useMemo(() => {
        if (ukmId) {
            return ukm?.content.find((li) => li.id === ukmId);
        }
        return ukm?.content[0];
    }, [ukm?.content, ukmId]);

    const isFetchingItems = useTransaksiStore((s) => s.isFetchingItems);
    const cancelFetchingItems = useTransaksiStore((s) => s.cancelFetchingItems);

    const [dataStok, setDataStok] = useState<ModalStokField>();
    const [isOpenModalStok, setIsOpenModalStok] = useState(false);
    const [detailModalState, setDetailModalState] = useState<{
        open: boolean;
        namaBarang: string;
        url?: string;
        deskripsi?: string;
    }>({
        open: false,
        namaBarang: "",
        url: "",
        deskripsi: "",
    });

    const openModalStok = useCallback((data?: ModalStokField) => {
        if (data) {
            setDataStok((prev) => ({
                ...prev,
                ...data,
            }));
        }
        setIsOpenModalStok(true);
    }, []);

    const closeModalStok = () => {
        setIsOpenModalStok(false);
        setDataStok(undefined);
    };

    const [isOpenModalHapusStok, setIsOpenModalHapusStok] = useState(false);
    const [stokToDelete, setStokToDelete] = useState<{
        id?: number | string;
        nama: string;
    }>({
        id: undefined,
        nama: "",
    });

    const openModalHapusStok = useCallback(
        (id: number | string | undefined, nama: string) => {
            setStokToDelete({
                id,
                nama,
            });
            setIsOpenModalHapusStok(true);
        },
        [],
    );

    const closeModalHapusStok = useCallback(() => {
        setIsOpenModalHapusStok(false);
        setStokToDelete({
            id: undefined,
            nama: "",
        });
    }, []);

    const handleSortModelChange = useCallback(
        (column: ParamsGetItem["column"], sortBy: ParamsGetItem["sortby"]) => {
            setUrlState({
                sortColumn: column ?? "",
                sortDir: sortBy ?? "",
            });
        },
        [setUrlState],
    );

    const handleOpenDetailBarang = useCallback(
        (namaBarang: string, url: string, deskripsi?: string) => {
            setDetailModalState({
                open: true,
                namaBarang,
                url,
                deskripsi,
            });
        },
        [],
    );

    const handleCloseDetailBarang = useCallback(() => {
        setDetailModalState((prev) => ({
            ...prev,
            open: false,
        }));
    }, []);

    const handleSearchSubmit = useCallback(() => {
        setUrlState({ search: searchInput, page: 1 });
    }, [searchInput, setUrlState]);

    const [selectedProducts, setSelectedProducts] = useState<
        SelectedProductBundling[]
    >([]);

    const isSelected = useCallback(
        (id: number) => {
            return selectedProducts.some((li) => li.id === id);
        },
        [selectedProducts],
    );

    const handleSelectedProducts = useCallback(
        (id: number, nama: string, harga: number, stok: number) => {
            setSelectedProducts((prev) => {
                if (isSelected(id)) {
                    return prev.filter((li) => li.id !== id);
                } else {
                    return [
                        ...prev,
                        {
                            id,
                            nama,
                            harga,
                            stok,
                        },
                    ];
                }
            });
        },
        [isSelected],
    );

    const handleClearSelectedProducts = useCallback(() => {
        setSelectedProducts([]);
    }, []);

    const [isOpenModalDeleteMultipleStok, setIsOpenModalDeleteMultipleStok] =
        useState(false);

    const openModalDeleteMultipleStok = useCallback(() => {
        setIsOpenModalDeleteMultipleStok(true);
    }, []);

    const closeModalDeleteMultipleStok = useCallback(() => {
        setIsOpenModalDeleteMultipleStok(false);
    }, []);

    const normalizedItems = useMemo(() => {
        if (!dataItem?.content) {
            return [];
        }
        return dataItem.content.map((item, index) => ({
            ...item,
            idx: index + 1,
        }));
    }, [dataItem]);

    const isAssetProdukVisible = useMemo(() => {
        if (!user) {
            return false;
        }
        return (
            user.aksesAssetProduk === 1 &&
            user.akses?.assetProduk?.includes(111)
        );
    }, [user]);

    const renderSelectCell = useCallback(
        (params: any) => (
            <IconButton
                onClick={() => {
                    handleSelectedProducts(
                        params.row.id,
                        params.row.namaBarang,
                        params.row.harga,
                        params.row.stok,
                    );
                }}
                sx={{
                    minWidth: "unset",
                    padding: "6px",
                    width: "fit-content",
                }}
                size="small"
                color="primary"
            >
                {isSelected(params.row.id) ? (
                    <CheckBoxIcon />
                ) : (
                    <CheckBoxOutlineBlankIcon />
                )}
            </IconButton>
        ),
        [handleSelectedProducts, isSelected],
    );

    const renderInfoCell = useCallback((params: any) => {
        return (
            <Stack
                direction="row"
                paddingLeft={1}
                spacing={1}
                marginTop={3}
                sx={{ marginBottom: params.row.favorite > 0 ? -1 : 3 }}
            >
                <Box>
                    <Avatar
                        sx={{
                            backgroundColor: "primary.main",
                            width: 50,
                            height: 55,
                        }}
                        variant="rounded"
                        alt={params.value}
                        src={params.row.gambar}
                    >
                        {avatarAlt(params.value)}
                    </Avatar>
                    {params.row.favorite > 0 && (
                        <StarIcon
                            sx={{
                                position: "relative",
                                zIndex: 2,
                                bottom: 60,
                                left: "70%",
                                border: 1,
                                borderColor: "#e7e5e4",
                                borderRadius: "50%",
                                padding: 0.2,
                                color: "#fbbf24",
                                bgcolor: "#FFFFFF",
                            }}
                            fontSize="medium"
                        />
                    )}
                </Box>
                <Stack direction="column" spacing={0.3}>
                    <Box>
                        <Tooltip
                            title={`${params.value} ${
                                params.row.terjual
                                    ? `(${params.row.terjual})`
                                    : ""
                            }`}
                        >
                            <Typography
                                component="div"
                                variant="subtitle2"
                                fontWeight="bold"
                            >
                                {`${truncate(params.value, 15)} `}
                                <Typography
                                    variant="subtitle2"
                                    display="inline"
                                >
                                    {`${
                                        params.row.terjual
                                            ? `(${params.row.terjual})`
                                            : ""
                                    }`}
                                </Typography>
                            </Typography>
                        </Tooltip>
                    </Box>
                    <Box>
                        <Tooltip title={params.row.deskripsi ?? ""}>
                            <Typography variant="caption">
                                {params.row.deskripsi === null
                                    ? ""
                                    : truncate(params.row.deskripsi, 25)}
                            </Typography>
                        </Tooltip>
                    </Box>
                </Stack>
            </Stack>
        );
    }, []);

    const renderKategoriCell = useCallback((params: any) => {
        return (
            <Tooltip title={params.row.kategori.nama ?? ""}>
                <span className="table-cell-trucate">
                    {params.row.kategori.nama ?? ""}
                </span>
            </Tooltip>
        );
    }, []);

    const renderHargaCell = useCallback((params: any) => {
        const harga =
            params.value.length > 0
                ? Number(params.value[0].harga)
                : params.row.harga
                  ? Number(params.row.harga)
                  : 0;
        const hargaText =
            params.value.length > 0
                ? toRupiah(Number(params.value[0].harga))
                : params.row.harga
                  ? toRupiah(params.row.harga)
                  : "-";
        return (
            <Tooltip title={harga ? toRupiah(harga) : "-"}>
                <span className="table-cell-trucate">{hargaText}</span>
            </Tooltip>
        );
    }, []);

    const renderStokCell = useCallback((params: any) => {
        return (
            <Tooltip
                title={
                    params.row.isBundling > 0
                        ? "Otomatis: Stok dihitung dari stok barang bundling"
                        : params.value === null
                          ? "Nonaktif: Barang ini tidak menggunakan stok"
                          : params.value
                }
            >
                <Typography
                    variant="subtitle2"
                    color={
                        params.value < 5 && params.value !== null
                            ? "#ef4444"
                            : "#76747C"
                    }
                    fontWeight={
                        params.value < 5 && params.value !== null
                            ? 700
                            : "normal"
                    }
                >
                    {params.row.isBundling > 0
                        ? "Otomatis"
                        : params.value === null
                          ? "Nonaktif"
                          : params.value}
                </Typography>
            </Tooltip>
        );
    }, []);

    const renderNilaiAsetCell = useCallback((params: any) => {
        const value =
            params.value !== undefined && params.value !== null
                ? toRupiah(params.value)
                : "-";
        return (
            <Tooltip title={value}>
                <span className="table-cell-trucate">{value}</span>
            </Tooltip>
        );
    }, []);

    const renderSkuCell = useCallback((params: any) => {
        return (
            <Tooltip title={params.value ?? "-"}>
                <span className="table-cell-trucate">{params.value}</span>
            </Tooltip>
        );
    }, []);

    const renderExpCell = useCallback((params: any) => {
        return (
            <Tooltip
                title={params.value ? moment(params.value).format("LL") : "-"}
            >
                <Typography variant="body2">
                    {params.value ? moment(params.value).format("LL") : "-"}
                </Typography>
            </Tooltip>
        );
    }, []);

    const renderActionCell = useCallback(
        (params: any) => {
            const row: ContentGetItemResponse = params.row;

            return (
                <Stack direction="row" spacing={2}>
                    <Button
                        variant="contained"
                        onClick={() =>
                            onOpenPrintBarcode({
                                sku: row.sku,
                                namaBarang: truncate(row.namaBarang, 22, true),
                                hargaBarang: `Rp${toRibuan(row.harga)}`,
                            })
                        }
                        size="small"
                        color="buttonblue"
                        disableElevation
                        sx={{
                            minWidth: "unset",
                            padding: "8px",
                            width: "32px",
                            height: "32px",
                        }}
                    >
                        <PrintIcon sx={{ width: "16px", height: "16px" }} />
                    </Button>
                    <Button
                        disabled={user && !user.akses.stok.includes(25)}
                        variant="contained"
                        onClick={() =>
                            openModalStok({
                                id: row.id,
                                namaBarang: row.namaBarang,
                                deskripsiBarang: row.deskripsi,
                                kategoriId: row.kategori,
                                harga: row.harga,
                                sku: row.sku,
                                jumlahStok: row.stok,
                                isActiveStok: row.isActiveStok,
                                itemSatuan: null,
                                isBundling: row.isBundling,
                                detailBundling: row.detailBundling ?? [],
                                favorite: row.favorite === 1,
                                isShow: row.isShow === 1,
                                hargaMultiple: row.hargaMultiple.map((li) => ({
                                    id: li.id,
                                    harga: Number(li.harga),
                                    keterangan: li.keterangan,
                                })),
                            })
                        }
                        size="small"
                        color="buttonyellow"
                        sx={{
                            minWidth: "unset",
                            padding: "8px",
                            width: "32px",
                            height: "32px",
                        }}
                    >
                        <EditIcon sx={{ width: "16px", height: "16px" }} />
                    </Button>
                    <Button
                        disabled={user && !user.akses.stok.includes(26)}
                        variant="contained"
                        onClick={() =>
                            openModalHapusStok(params.id, row.namaBarang)
                        }
                        size="small"
                        color="buttonred"
                        sx={{
                            minWidth: "unset",
                            padding: "8px",
                            width: "32px",
                            height: "32px",
                        }}
                    >
                        <DeleteIcon sx={{ width: "16px", height: "16px" }} />
                    </Button>
                </Stack>
            );
        },
        [openModalStok, openModalHapusStok, onOpenPrintBarcode, user],
    );

    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: "selector",
                headerName: "",
                headerAlign: "center",
                align: "center",
                minWidth: 50,
                sortable: false,
                disableColumnMenu: true,
                renderCell: renderSelectCell,
            },
            {
                field: "namaBarang",
                headerName: "Info Barang (Terjual)",
                headerAlign: "center",
                flex: isLaptopScreen ? 1.5 : undefined,
                minWidth: 250,
                valueGetter: (params) => params.value,
                renderCell: renderInfoCell,
            },
            {
                field: "kategori",
                headerName: "Kategori",
                headerAlign: "center",
                align: "center",
                flex: isLaptopScreen ? 1 : undefined,
                minWidth: 130,
                valueGetter: (params) => params.row.kategori.nama,
                renderCell: renderKategoriCell,
            },
            {
                field: "hargaMultiple",
                headerName: "Harga",
                headerAlign: "center",
                align: "right",
                flex: isLaptopScreen ? 1 : undefined,
                minWidth: 130,
                valueGetter: (params) =>
                    params.value.length > 0
                        ? Number(params.value[0].harga)
                        : params.row.harga
                          ? Number(params.row.harga)
                          : 0,
                renderCell: renderHargaCell,
            },
            {
                field: "stok",
                headerName: "Stok",
                headerAlign: "center",
                align: "center",
                flex: isLaptopScreen ? 1 : undefined,
                minWidth: 100,
                renderCell: renderStokCell,
            },
            {
                field: "totalHpp",
                headerName: "Nilai Aset",
                headerAlign: "center",
                align: "right",
                flex: isLaptopScreen ? 1 : undefined,
                minWidth: 140,
                hide: !isAssetProdukVisible,
                renderCell: renderNilaiAsetCell,
            },
            {
                field: "sku",
                headerName: "SKU",
                headerAlign: "center",
                align: "center",
                flex: isLaptopScreen ? 1 : undefined,
                minWidth: 150,
                renderCell: renderSkuCell,
            },
            {
                field: "expAt",
                headerName: "Tanggal Kadaluarsa",
                align: "center",
                minWidth: 190,
                renderCell: renderExpCell,
            },
            {
                field: "aksi",
                headerName: "Aksi",
                headerAlign: "center",
                align: "center",
                minWidth: 160,
                sortable: false,
                disableColumnMenu: true,
                renderCell: renderActionCell,
            },
        ],
        [
            isAssetProdukVisible,
            isLaptopScreen,
            renderActionCell,
            renderHargaCell,
            renderInfoCell,
            renderKategoriCell,
            renderNilaiAsetCell,
            renderSelectCell,
            renderSkuCell,
            renderStokCell,
            renderExpCell,
        ],
    );

    const handleOpenModalKategori = useCallback(() => {
        onOpenKategori();
        refetchKategori();
    }, [onOpenKategori, refetchKategori]);

    const actionButtonConfigs = useMemo<ActionButtonConfig[]>(
        () => [
            {
                key: "mutasi",
                label: "Mutasi Stok",
                icon: MoveToInboxIcon,
                onClick: onOpenMutasi,
                permissionId: 22,
            },
            {
                key: "katalog",
                label: "Buat Katalog",
                icon: AssignmentReturnedIcon,
                onClick: onOpenKatalog,
                permissionId: 23,
            },
            {
                key: "unggah",
                label: "Unggah Stok",
                icon: FileUploadIcon,
                onClick: onOpenImport,
            },
            {
                key: "kategori",
                label: "Atur Kategori",
                icon: SettingsIcon,
                onClick: handleOpenModalKategori,
                permissionId: 24,
            },
        ],
        [
            handleOpenModalKategori,
            onOpenImport,
            onOpenKatalog,
            onOpenMutasi,
        ],
    );

    const isActionDisabled = useCallback(
        (permissionId?: number) => {
            if (!permissionId || !user) {
                return false;
            }
            const stokAccess = user.akses?.stok ?? [];
            return !stokAccess.includes(permissionId);
        },
        [user],
    );

    const isAddButtonDisabled = useMemo(() => {
        if (!ukmData) {
            return false;
        }
        const stokAccess = user?.akses?.stok ?? [];
        const lacksPermission = user ? !stokAccess.includes(21) : false;
        if (lacksPermission) {
            return true;
        }
        if (!dataItem) {
            return false;
        }
        const limit =
            ukmData.limitItem === null
                ? 500
                : ukmData.limitItem ?? 0;
        if (!limit) {
            return false;
        }
        return dataItem.totalItem >= limit;
    }, [dataItem, ukmData, user]);

    const stockLimitInfo = useMemo<StockLimitInfo | null>(() => {
        if (!ukmData || !dataItem) {
            return null;
        }
        const hasCustomLimit = Boolean(ukmData.limitItem);
        const limitValue = hasCustomLimit
            ? Number(ukmData.limitItem)
            : 500;
        if (!limitValue) {
            return null;
        }
        if (dataItem.totalItem >= limitValue) {
            return {
                label: "Stok Max",
                color: "error",
                fontWeight: "bold",
            };
        }
        const label = `${dataItem.totalItem}/${limitValue}`;
        if (dataItem.totalItem >= limitValue - 10) {
            return {
                label,
                color: "error",
                fontWeight: "bold",
            };
        }
        return {
            label,
            color: "GrayText",
        };
    }, [dataItem, ukmData]);

    const renderStockSummary = useCallback(
        (options?: { isMobile?: boolean }) => {
            const displayProp = options?.isMobile
                ? "flex"
                : { xs: "none", sm: "flex" };
            return (
                <Box
                    display={displayProp}
                    alignItems="center"
                    gap={2}
                    flexDirection="row"
                >
                    <Box width="max-content">
                        {stockLimitInfo && (
                            <Typography
                                color={stockLimitInfo.color}
                                fontWeight={stockLimitInfo.fontWeight}
                            >
                                {stockLimitInfo.label}
                            </Typography>
                        )}
                    </Box>
                    <Button
                        disabled={isAddButtonDisabled}
                        variant="contained"
                        onClick={() => openModalStok()}
                        startIcon={<AddIcon />}
                        size="medium"
                        sx={
                            options?.isMobile
                                ? { objectFit: "contain" }
                                : { lineHeight: 2 }
                        }
                    >
                        Tambah
                    </Button>
                </Box>
            );
        },
        [isAddButtonDisabled, openModalStok, stockLimitInfo],
    );

    const renderActionButtons = useCallback(
        (variant: ActionVariant) => {
            const isTablet = variant === "tablet";
            const isDesktop = variant === "desktop";
            const buttonSize = isTablet ? "small" : "medium";
            const spacingValue: StackProps["spacing"] =
                variant === "desktop" ? { xs: 0, sm: 1 } : 1;
            const justifyContent =
                variant === "phone"
                    ? "flex-end"
                    : isTablet
                      ? "space-around"
                      : undefined;
            return (
                <Stack
                    display="flex"
                    direction="row"
                    spacing={spacingValue}
                    justifyContent={justifyContent}
                >
                    {actionButtonConfigs.map((action) => {
                        const IconComponent = action.icon;
                        const showLabel = isDesktop;
                        return (
                            <Button
                                key={action.key}
                                disabled={isActionDisabled(
                                    action.permissionId,
                                )}
                                variant="outlined"
                                onClick={action.onClick}
                                size={buttonSize}
                                startIcon={
                                    showLabel ? (
                                        <IconComponent />
                                    ) : undefined
                                }
                                sx={
                                    showLabel
                                        ? { backgroundColor: "#ffffff" }
                                        : {
                                              backgroundColor: "#ffffff",
                                              minWidth: 0,
                                              objectFit: "contain",
                                              padding: isTablet
                                                  ? "6px"
                                                  : undefined,
                                          }
                                }
                            >
                                {showLabel ? (
                                    action.label
                                ) : (
                                    <IconComponent />
                                )}
                            </Button>
                        );
                    })}
                </Stack>
            );
        },
        [actionButtonConfigs, isActionDisabled],
    );

    const handleOnPagination = useCallback((newPage: number) => {
        setUrlState({ page: newPage + 1 });
    }, [setUrlState]);

    const handleOnSizeChange = useCallback((size: number) => {
        setUrlState({ pageSize: size, page: 1 });
    }, [setUrlState]);

    useEffect(() => {
        if (isFetchingItems === true) {
            refetchItem();
            refetchKategori();
            cancelFetchingItems();
        }
    }, [cancelFetchingItems, isFetchingItems, refetchItem, refetchKategori]);

    useEffect(() => {
        if (ukmIdUser) {
            setUrlState({ page: 1 });
        }
    }, [ukmIdUser, setUrlState]);

    const isError = isErrorItem || isErrorKategori;

    if (statusKategori === "loading") {
        return (
            <Box
                position="relative"
                height={isLaptopScreen ? "100vh" : "calc(100vh - 56px)"}
            >
                <Spinner />
            </Box>
        );
    }

    if (isError) {
        Swal.fire({
            title: "Maaf, kami tidak dapat memproses permintaan anda",
            text: `Silahkan coba lagi dengan memuat ulang halaman`,
            position: "top-end",
            showConfirmButton: false,
            icon: "error",
            toast: true,
            width: 600,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
                container: "my-swal",
            },
        });
    }

    return (
        <React.Fragment>
            {isPhoneScreen ? (
                <Stack
                    display="flex"
                    justifyContent="space-between"
                    direction={{ xs: "column", sm: "row", md: "row" }}
                >
                    <Stack
                        my={2}
                        direction="row"
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography fontSize="h6" fontWeight="bold">
                            Daftar Stok
                        </Typography>
                        {renderStockSummary({ isMobile: true })}
                    </Stack>
                    {renderActionButtons("phone")}
                </Stack>
            ) : isTabletScreen ? (
                <Stack
                    display="flex"
                    justifyContent="space-between"
                    marginTop={2}
                    direction={{ xs: "column", sm: "row", md: "row" }}
                >
                    <Typography variant="h6" fontWeight="bold">
                        Daftar Stok
                    </Typography>
                    {renderActionButtons("tablet")}
                </Stack>
            ) : (
                <Stack
                    display="flex"
                    justifyContent="space-between"
                    direction={{ xs: "column", sm: "row", md: "row" }}
                    marginTop={2}
                >
                    <Typography variant="h6" fontWeight="bold">
                        Daftar Stok
                    </Typography>
                    {renderActionButtons("desktop")}
                </Stack>
            )}
            <Stack>
                Kelola informasi produk dan pantau sisa stok produk.
            </Stack>
            <Box alignItems="center" marginY={2}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    spacing={2}
                    alignItems="center"
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={4} md={3}>
                            <TextField
                                placeholder="Cari barang..."
                                size="small"
                                value={searchInput}
                                onChange={(
                                    event: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                    setSearchInput(event.target.value);
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleSearchSubmit();
                                    }
                                }}
                                variant="outlined"
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={handleSearchSubmit}
                                            >
                                                <Search />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                    style: {
                                        backgroundColor: "white",
                                    },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4} md={4} lg={4} xl={2}>
                            <FormControl size="small" fullWidth>
                                <Autocomplete
                                    options={dataKategori?.content ?? []}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Kategori"
                                            variant="outlined"
                                        />
                                    )}
                                    getOptionLabel={(option) => option.nama}
                                    size="small"
                                    fullWidth
                                    value={selectedKategori}
                                    onChange={(
                                        _event,
                                        newKategori: IOptions | null,
                                    ) => {
                                        setUrlState({
                                            kategori: newKategori
                                                ? String(newKategori.id)
                                                : "",
                                            page: 1,
                                        });
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={3} sm={1}></Grid>
                    </Grid>
                    {renderStockSummary()}
                </Stack>
            </Box>

            <Box
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                sx={{
                    mt: 4,
                    mb: 4,
                    padding: 2,
                    borderRadius: 2,
                    border: "1px solid #69CD9E",
                    backgroundColor: "#DCF6EA",
                    gap: { xs: 1, sm: 0 },
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    {!isLoadingAsetRevenue ? (
                        selectedKategori
                            ? `Kategori: ${selectedKategori.nama}`
                            : "Semua Kategori"
                    ) : (
                        <Skeleton
                            variant="text"
                            width={120}
                            height={28}
                        />
                    )}
                </Stack>

                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={{ xs: 1, sm: 2 }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    sx={{
                        width: { xs: "100%", sm: "auto" },
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                            width: { xs: "100%", sm: "auto" },
                            flexWrap: "wrap",
                        }}
                    >
                        <Box
                            sx={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                bgcolor: "#45A779",
                                mr: 1,
                                flexShrink: 0,
                            }}
                        />

                        <Typography
                            sx={{
                                fontSize: { xs: "0.875rem", sm: "1rem" },
                                whiteSpace: "nowrap",
                            }}
                        >
                            Total Pendapatan:
                        </Typography>

                        {!isLoadingAsetRevenue ? (
                            <Typography
                                fontWeight="bold"
                                sx={{
                                    fontSize: { xs: "0.875rem", sm: "1rem" },
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {toRupiah(
                                    Number(asetRevenue?.totalPendapatan ?? 0)
                                )}
                            </Typography>
                        ) : (
                            <Skeleton
                                variant="text"
                                width={100}
                                height={28}
                            />
                        )}
                    </Stack>
                </Stack>
            </Box>
            
            <Collapse in={selectedProducts.length > 0}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: 2,
                        backgroundColor: "#f0f0f0",
                        borderRadius: "4px",
                        marginTop: 2,
                        marginBottom: 1,
                        justifyContent: "space-between",
                    }}
                >
                    <Stack direction="row" alignItems="center">
                        <IconButton
                            onClick={handleClearSelectedProducts}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                        <Typography sx={{ marginLeft: 1 }}>
                            {selectedProducts.length} terpilih
                        </Typography>
                    </Stack>
                    <Stack>
                        <Button
                            variant="contained"
                            sx={{
                                marginLeft: 2,
                                backgroundColor: "#FF7373",
                                "&:hover": {
                                    backgroundColor: "#FF7373",
                                },
                            }}
                            onClick={openModalDeleteMultipleStok}
                        >
                            Hapus Terpilih
                        </Button>
                    </Stack>
                </Box>
            </Collapse>
            {isPhoneScreen || isTabletScreen ? (
                isLoadingItem ? (
                    <Box position="relative" height="30vh">
                        <Spinner />
                    </Box>
                ) : dataItem && dataItem.totalElements > 0 ? (
                    <React.Fragment>
                        {normalizedItems.map((items) => (
                            <CardStokBarang
                                key={items.id}
                                data={items}
                                handleSelectedProducts={handleSelectedProducts}
                                isSelected={isSelected}
                                onEdit={openModalStok}
                                onDelete={openModalHapusStok}
                                onOpenDetail={handleOpenDetailBarang}
                                onOpenPrintBarcode={onOpenPrintBarcode}
                            />
                        ))}
                        <Box
                            display="flex"
                            justifyContent="center"
                            paddingY={2}
                        >
                            <Pagination
                                color="primary"
                                count={
                                    dataItem
                                        ? Math.ceil(
                                              Number(
                                                  dataItem.totalElements /
                                                      urlState.pageSize,
                                              ),
                                          )
                                        : 0
                                }
                                page={urlState.page}
                                onChange={(_event, value) => {
                                    setUrlState({ page: value });
                                }}
                                siblingCount={0}
                            />
                        </Box>
                    </React.Fragment>
                ) : (
                    <Stack
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                        spacing={2}
                        paddingTop={2}
                    >
                        <BarangTidakDitemukan width="150px" height="150px" />
                        <Typography fontWeight="bold">
                            Barang tidak ditemukan
                        </Typography>
                    </Stack>
                )
            ) : (
                <Box
                    sx={{
                        height: 820,
                        width: "100%",
                        marginBottom: 3,
                        backgroundColor: "#ffffff",
                        borderRadius: 1,
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: "bold",
                        },
                        "& .MuiDataGrid-cell:focus-within": {
                            outline: "none !important",
                        },
                        "& .MuiDataGrid-iconButtonContainer": {
                            visibility: "visible",
                            width: "0 !important",
                        },
                    }}
                >
                    <DataGrid
                        pagination
                        components={{
                            ColumnUnsortedIcon: () => {
                                return <ImportExportIcon />;
                            },
                            NoRowsOverlay: NoRowsImage,
                            LoadingOverlay: LoadingTabel,
                        }}
                        rows={normalizedItems}
                        columns={columns}
                        disableColumnMenu
                        disableSelectionOnClick
                        paginationMode="server"
                        rowsPerPageOptions={[urlState.pageSize]}
                        onPageSizeChange={handleOnSizeChange}
                        onPageChange={handleOnPagination}
                        rowCount={dataItem?.totalElements ?? 0}
                        page={urlState.page - 1}
                        pageSize={urlState.pageSize}
                        loading={isLoadingItem}
                        density="comfortable"
                        sortModel={sortModel}
                        onSortModelChange={(newModel) => {
                            setSortModel(newModel);
                            handleSortModelChange(
                                mapStockFieldSorting(newModel[0]?.field ?? ""),
                                newModel[0]?.sort,
                            );
                        }}
                    />
                </Box>
            )}
            {isOpenModalStok && (
                <ModalStok
                    open={isOpenModalStok}
                    onClose={closeModalStok}
                    dataStok={dataStok}
                    onOpenMutasi={onOpenMutasi}
                />
            )}
            {isOpenModalHapusStok && (
                <ModalDeleteStok
                    refetch={refetchItem}
                    open={isOpenModalHapusStok}
                    onClose={closeModalHapusStok}
                    stokName={stokToDelete.nama}
                    stokId={stokToDelete.id}
                />
            )}
            {isOpenModalDeleteMultipleStok && (
                <ModalDeleteMultipleStok
                    refetch={refetchItem}
                    handleCollapseClose={handleClearSelectedProducts}
                    open={isOpenModalDeleteMultipleStok}
                    onClose={closeModalDeleteMultipleStok}
                    selectedProducts={selectedProducts}
                />
            )}
            {detailModalState.open && (
                <ModalDetailBarang
                    open={detailModalState.open}
                    onClose={handleCloseDetailBarang}
                    namaBarang={detailModalState.namaBarang}
                    deskripsi={detailModalState.deskripsi}
                    url={detailModalState.url}
                />
            )}
        </React.Fragment>
    );
};

export default TabelBarang;
